/**
 * 印刷キューサービス
 *
 * IndexedDB を使って失敗したレシート/キッチンチケットの印刷ジョブを保存し、
 * 後からリトライできるようにする。
 */

const DB_NAME = 'posplus_print_queue';
const DB_VERSION = 1;
const STORE_NAME = 'jobs';
const MAX_RETRIES = 5;
const RETRY_INTERVAL_MS = 30000; // 30秒

/**
 * IndexedDB を開く
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      return reject(new Error('IndexedDB is not available'));
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

/**
 * ジョブをキューに追加
 * @param {{ type: 'receipt'|'kitchen', printerConfig: object, xmlData: string, metadata?: object }} job
 */
const enqueue = async (job) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const record = {
        type: job.type,
        printerConfig: job.printerConfig,
        xmlData: job.xmlData,
        metadata: job.metadata || {},
        status: 'pending',
        retries: 0,
        createdAt: new Date().toISOString(),
        lastAttemptAt: null,
        error: null,
      };

      const request = store.add(record);
      request.onsuccess = () => {
        console.log('[PrintQueue] ジョブをキューに追加:', record.type);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[PrintQueue] キュー追加エラー:', error);
  }
};

/**
 * 未処理ジョブの一覧を取得
 */
const getPendingJobs = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('status');
      const request = index.getAll('pending');

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[PrintQueue] 未処理ジョブ取得エラー:', error);
    return [];
  }
};

/**
 * ジョブのステータスを更新
 */
const updateJob = async (id, updates) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const record = getRequest.result;
        if (!record) {
          return resolve(null);
        }

        const updated = { ...record, ...updates };
        const putRequest = store.put(updated);
        putRequest.onsuccess = () => resolve(updated);
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('[PrintQueue] ジョブ更新エラー:', error);
  }
};

/**
 * 完了したジョブを削除
 */
const removeJob = async (id) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[PrintQueue] ジョブ削除エラー:', error);
  }
};

/**
 * 全ジョブを取得（管理画面用）
 */
const getAllJobs = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[PrintQueue] 全ジョブ取得エラー:', error);
    return [];
  }
};

/**
 * 完了・失敗済みジョブをクリーンアップ（24時間以上前のもの）
 */
const cleanup = async () => {
  try {
    const db = await openDB();
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.openCursor();
      let removed = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const record = cursor.value;
          if (
            (record.status === 'completed' || record.status === 'failed') &&
            record.createdAt < cutoff
          ) {
            cursor.delete();
            removed++;
          }
          cursor.continue();
        } else {
          if (removed > 0) {
            console.log(`[PrintQueue] ${removed}件の古いジョブを削除`);
          }
          resolve(removed);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[PrintQueue] クリーンアップエラー:', error);
  }
};

/**
 * リトライ処理（sendToPrinter関数を受け取る）
 * @param {Function} sendToPrinter - (printerConfig, xmlData) => Promise
 */
const processQueue = async (sendToPrinter) => {
  const jobs = await getPendingJobs();
  if (jobs.length === 0) return { processed: 0, succeeded: 0, failed: 0 };

  console.log(`[PrintQueue] ${jobs.length}件の未処理ジョブをリトライ`);

  let succeeded = 0;
  let failed = 0;

  for (const job of jobs) {
    if (job.retries >= MAX_RETRIES) {
      await updateJob(job.id, { status: 'failed', error: `最大リトライ回数(${MAX_RETRIES})に達しました` });
      failed++;
      continue;
    }

    try {
      await sendToPrinter(job.printerConfig, job.xmlData);
      await removeJob(job.id);
      console.log(`[PrintQueue] ジョブ #${job.id} リトライ成功`);
      succeeded++;
    } catch (error) {
      await updateJob(job.id, {
        retries: job.retries + 1,
        lastAttemptAt: new Date().toISOString(),
        error: error.message,
      });
      console.warn(`[PrintQueue] ジョブ #${job.id} リトライ失敗 (${job.retries + 1}/${MAX_RETRIES}):`, error.message);
      failed++;
    }
  }

  return { processed: jobs.length, succeeded, failed };
};

/**
 * 定期リトライタイマーを開始
 * @param {Function} sendToPrinter - (printerConfig, xmlData) => Promise
 * @returns {number} intervalId
 */
let retryIntervalId = null;

const startRetryTimer = (sendToPrinter) => {
  if (retryIntervalId) return retryIntervalId;

  retryIntervalId = setInterval(async () => {
    try {
      const result = await processQueue(sendToPrinter);
      if (result.processed > 0) {
        console.log(`[PrintQueue] リトライ結果: ${result.succeeded}成功 / ${result.failed}失敗`);
      }
    } catch (error) {
      console.error('[PrintQueue] リトライタイマーエラー:', error);
    }
  }, RETRY_INTERVAL_MS);

  // 1日1回クリーンアップ
  setInterval(() => cleanup(), 24 * 60 * 60 * 1000);

  console.log(`[PrintQueue] リトライタイマー開始 (${RETRY_INTERVAL_MS / 1000}秒間隔)`);
  return retryIntervalId;
};

const stopRetryTimer = () => {
  if (retryIntervalId) {
    clearInterval(retryIntervalId);
    retryIntervalId = null;
  }
};

const printQueue = {
  enqueue,
  getPendingJobs,
  getAllJobs,
  processQueue,
  removeJob,
  cleanup,
  startRetryTimer,
  stopRetryTimer,
};

export default printQueue;
